import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../components/ChatMessage';
import { ChatInput } from '../components/ChatInput';
import axiosInstance from 'api/axios';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import LoadingDots from '../components/LoadingDots';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, EllipsisVertical } from 'lucide-react';
export interface ChatPayload {
  query: any;
  id?: string | null;
}
const ChatContainer = () => {
  const [messages, setMessages] = useState<
    {
      index: number;
      isUser: boolean;
      content: string;
      userName: string;
      image: string;
    }[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);

  // Function to get message by index
  const getMessageByIndex = (targetIndex: number) => {
    // Check initial bot message
    if (targetIndex === 0) {
      return {
        index: 0,
        isUser: false,
        content: 'Hello, what can I help you with?',
        userName: 'Kreaitor',
        image: '/logo.png',
      };
    }
    // Check messages array
    return messages.find((message) => message.index === targetIndex);
  };

  // Function to access and use a specific message
  const handleRegenerate = async (index: number) => {
    const message = getMessageByIndex(index);
    if (message) {
      await sendMessage(message.content);
      toast.success('Regenerated Successfully');
    } else {
      console.log(`No message found at index ${index}`);
      return null;
    }
  };

  const handleEndConversation = () => {
    localStorage.removeItem('fileId');
    navigate('/chat-with-docs/get-started');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleExport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printStyles = `
      <style>
        body { 
          padding: 20px;
          font-family: Arial, sans-serif;
          max-width: 800px;
          margin: 0 auto;
        }
        .chat-bubble {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          position: relative;
          max-width: 80%;
        }
        .user-message {
          background-color: #D7D6FD;
          margin-left: auto;
        }
        .assistant-message {
          background-color: #1f2937;
          color: white;
        }
        .message-container {
          margin-bottom: 24px;
        }
        .user-info {
          font-weight: 500;
          margin-bottom: 8px;
          text-align:right;
        }
        .bot-info {
          font-weight: 500;
          margin-bottom: 8px;
          text-align:left;
        }
        .header {
          text-align: center;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e5e7eb;
        }
        .footer {
          margin-top: 32px;
          text-align: right;
          color: #6b7280;
          font-size: 14px;
          border-top: 1px solid #e5e7eb;
          padding-top: 16px;
        }
          .user-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: #60A5FA;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 500;
        font-size: 16px;
      }
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      </style>
    `;

    const allMessages = [
      {
        index: 0,
        isUser: false,
        content: 'Hello, what can I help you with?',
        userName: 'Kreaitor',
      },
      ...messages,
    ];

    const messageElements = allMessages
      .map(
        (msg) => `
      <div class="message-container">
      ${msg.isUser ? '<div class="user-info">' : '<div class="bot-info">'}
                ${msg.userName} </div>
        <div class="chat-bubble ${msg.isUser ? 'user-message' : 'assistant-message'}">
          ${msg.content}
        </div>
      </div>
    `
      )
      .join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Chat Export - ${new Date().toLocaleDateString()}</title>
          ${printStyles}
        </head>
        <body>
          <div class="header">
            <h1>Chat Conversation</h1>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
          <div class="chat-content">
            ${messageElements}
          </div>
          <div class="footer">
            End of conversation - ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const sendMessage = async (content: any) => {
    if (!content.trim()) return;

    const userMessage = {
      index: currentIndex,
      isUser: true,
      content,
      userName: user?.displayName || 'New User',
      image: user?.imgUrl || '/logo.png',
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentIndex((prev) => prev + 1);
    setIsLoading(true);

    try {
      let payload: ChatPayload = {
        query: userMessage.content,
      };
      if (!user) {
        payload = {
          ...payload,
          id: localStorage.getItem('fileId'),
        };
      }
      console.log('payload', payload);
      const response = await axiosInstance.private.post(
        '/api/v1/chat-with-doc/chat',
        payload
      );

      if (!response) {
        throw new Error('Failed to get response');
      }

      const assistantMessage = {
        index: currentIndex + 1,
        isUser: false,
        content: response.data.data.response,
        userName: 'Kreaitor',
        image: '/logo.png',
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentIndex((prev) => prev + 2); // Increment by 2 to account for both messages
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        index: currentIndex + 1,
        isUser: false,
        content: 'Sorry, I encountered an error. Please try again.',
        userName: 'Kreaitor',
        image: '/logo.png',
      };
      setMessages((prev) => [...prev, errorMessage]);
      setCurrentIndex((prev) => prev + 2);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialMessage = () => {
      if (localStorage.getItem('chat1') && localStorage.getItem('chat2')) {
        const userMessage = {
          index: 1,
          isUser: true,
          content: localStorage.getItem('chat1') || '',
          userName: user?.displayName || 'New User',
          image: user?.imgUrl || '/logo.png',
        };
        const kreaitorMessage = {
          index: 2,
          isUser: false,
          content: localStorage.getItem('chat2') || '',
          userName: 'Kreaitor',
          image: '/logo.png',
        };
        setMessages((prev) => [...prev, userMessage, kreaitorMessage]);
        setCurrentIndex(3); // Set to next available index
        localStorage.removeItem('chat1');
        localStorage.removeItem('chat2');
      }
    };
    fetchInitialMessage();
  }, [user?.displayName, user?.imgUrl]);

  const handleShowDoc = () => {
    const pdfData = localStorage.getItem('filePath');
    if (pdfData) {
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
        <html>
          <head>
            <title>PDF Viewer</title>
          </head>
          <body style="margin:0;padding:0;">
            <embed 
              src="${pdfData}" 
              type="application/pdf" 
              width="100%" 
              height="100%"
              style="position:fixed;top:0;left:0;"
            />
          </body>
        `);
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-background-main">
      <header className="flex-none px-4 py-3 border-b">
        <div className="flex justify-between  mx-auto w-full">
          <div className="flex items-center gap-3">
            <button
              onClick={handleEndConversation}
              className="lg:p-1.5  rounded-lg transition-colors "
            >
              <ArrowLeft className="w-5 h-5 text-primary" />
            </button>
            <h1 className="text-[12px] text-text-primary lg:text-lg font-semibold">
              Chat With Kreaitor
            </h1>
          </div>
          <div className="flex text-[12px] lg:text-[24px] items-center gap-7">
            <button
              onClick={handleShowDoc}
              className="text-[12px] lg:text-[16px] text-primary  hover:underline font-medium underline"
            >
              The uploaded doc
            </button>
            <div className="hidden lg:block">
              <button
                onClick={handleExport}
                className="px-3 py-2 text-sm lg:text-[16px] text-white rounded-md bg-primary transition-colors"
              >
                Export
              </button>
            </div>
            <div className="lg:hidden relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="p-2 text-text-primary rounded-full transition-colors"
              >
                <EllipsisVertical />
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {/* <button
                    onClick={() => {
                      // Handle save functionality
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors"
                  >
                    Save
                  </button> */}
                  <button
                    onClick={() => {
                      handleExport();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors"
                  >
                    Export
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto px-2 sm:px-0">
          <div className="flex justify-center">
            <div className="w-full max-w-[1000px] p-2 sm:p-4 space-y-6">
              <ChatMessage
                isUser={false}
                content={'Hello, what can I help you with?'}
                userName={'Kreaitor'}
                image="/logo.png"
                handleRegenerate={handleRegenerate}
                index={0}
                isLatest={messages.length === 0}
              />
              {messages.map((message, idx) => {
                // Find the last non-user message
                const lastNonUserMessageIndex = [...messages]
                  .reverse()
                  .findIndex((m) => !m.isUser);
                const lastNonUserRealIndex =
                  lastNonUserMessageIndex !== -1
                    ? messages.length - 1 - lastNonUserMessageIndex
                    : -1;
                const isLatest =
                  !message.isUser && idx === lastNonUserRealIndex;

                return (
                  <ChatMessage
                    key={message.index}
                    isUser={message.isUser}
                    content={message.content}
                    userName={message.userName}
                    image={message.image}
                    handleRegenerate={handleRegenerate}
                    index={message.index}
                    isLatest={isLatest}
                  />
                );
              })}
              {isLoading && <LoadingDots />}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        <div className="bg-background-main px-3 sm:px-4 py-3 sm:py-4">
          <ChatInput onSend={sendMessage} disabled={isLoading} />
          <div
            className="text-center text-primary  underline cursor-pointer pt-3 text-sm font-medium "
            onClick={handleEndConversation}
          >
            End Conversation?
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;
